import { ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthStore } from '../../../stores/authStore';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useCurrentUser } from '../api/useAuth';
import { syncFromProfile } from '../../settings/hooks/usePreferences';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * AuthGuard - Protects routes that require authentication
 * Redirects unauthenticated users to /login
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  const { data: currentUser, isLoading } = useCurrentUser();
  const hasSynced = useRef(false);

  // Sync user profile preferences (locale, currency, etc.) on first load
  useEffect(() => {
    if (currentUser && !hasSynced.current) {
      hasSynced.current = true;
      syncFromProfile(currentUser);
    }
  }, [currentUser]);

  // Show loading spinner while checking authentication
  if (isLoading && isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render children if authenticated
  return <>{children}</>;
}
